import React from 'react';

/**
 * Raporty -> Magazyn. Liczby z GET /reports/products/warehouse-stock, ktory od audytu
 * 2026-09-13 korzysta z jednej definicji (services/warehouse_value_service.py):
 *   wartosc netto = SUM(partie FIFO: ilosc pozostala x cena zakupu netto), aktywne towary,
 *   brutto = netto x (1 + VAT produktu), kaucje osobno (poza wartoscia).
 * Linie "poza wartoscia glowna" (bez partii / ujemne / rozjazd / kaucje) NIE sa wliczane.
 */
export const formatCurrency = (value) => {
  const n = parseFloat(value || 0);
  const znak = n < 0 ? '-' : '';
  return znak + Math.abs(n).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

export const formatQuantity = (value) => {
  const n = Math.round(parseFloat(value || 0));
  const znak = n < 0 ? '-' : '';
  return znak + Math.abs(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
};

const linia = (l) => l || { produkty: 0, sztuki: 0, netto: 0 };

const th = (align = 'left', extra = {}) => ({
  padding: '0.4rem', textAlign: align, borderBottom: '2px solid var(--border)', whiteSpace: 'nowrap', ...extra,
});
const td = (align = 'left', extra = {}) => ({ padding: '0.4rem', textAlign: align, ...extra });

const Kafelek = ({ kolor, wartosc, etykieta, opis, testId }) => (
  <div
    data-testid={testId}
    title={opis}
    style={{
      backgroundColor: `var(--${kolor}-light)`, border: `1px solid var(--${kolor}-light)`,
      borderRadius: '0.375rem', padding: '1rem', textAlign: 'center',
    }}
  >
    <div style={{ fontSize: '1.4rem', fontWeight: '700', color: `var(--${kolor})` }}>{wartosc}</div>
    <div style={{ fontSize: '0.75rem', color: `var(--${kolor})` }}>{etykieta}</div>
  </div>
);

const WarehouseReport = ({ warehouseStock }) => {
  const summary = warehouseStock?.summary || {};
  const products = warehouseStock?.products || [];
  const categories = warehouseStock?.categories || [];
  const sklepy = warehouseStock?.sklepy || [];
  const bezPartii = linia(summary.bez_partii);
  const ujemne = linia(summary.ujemne);
  const rozjazd = linia(summary.rozjazd);
  const maNoweLinie = summary.bez_partii != null;
  // Glowne boxy = wartosc WG STANU z kartoteki (partie po odjeciu rozjazdu); FIFO w podpisie.
  const wgStanu = summary.wg_stanu || null;
  const nettoGlowne = wgStanu ? wgStanu.netto : summary.total_purchase_value_netto;
  const bruttoGlowne = wgStanu ? wgStanu.brutto : summary.total_purchase_value_brutto;
  const roznicaNetto = wgStanu ? (wgStanu.netto - (summary.total_purchase_value_netto || 0)) : 0;

  return (
    <div>
      {/* Podsumowanie */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>📦 Wartość magazynu (stan × cena zakupu netto z partii FIFO)</h5>
        </div>
        <div style={{ padding: '0.75rem' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: '0.75rem' }}>
            <Kafelek kolor="info" testId="kafelek-produkty" wartosc={formatQuantity(wgStanu ? wgStanu.produkty : summary.total_products)}
              etykieta={wgStanu ? `Produktów na stanie (z partiami: ${formatQuantity(summary.total_products)})` : 'Produktów z partiami FIFO'}
              opis="Aktywne towary ze stanem > 0; w nawiasie produkty mające partie FIFO" />
            <Kafelek kolor="orange" testId="kafelek-sztuki" wartosc={formatQuantity(wgStanu ? wgStanu.sztuki : summary.total_quantity)}
              etykieta={wgStanu ? `Sztuk na stanie (w partiach: ${formatQuantity(summary.total_quantity)})` : (maNoweLinie ? `Sztuk w partiach (stan łącznie: ${formatQuantity(summary.stan_sztuki)})` : 'Łączna ilość sztuk')}
              opis="Suma dodatnich stanów z kartoteki; w nawiasie suma ilości pozostałej w partiach FIFO" />
            <Kafelek kolor="success" testId="kafelek-netto" wartosc={`${formatCurrency(nettoGlowne)} zł`}
              etykieta={wgStanu ? `Wartość magazynu netto (wg partii: ${formatCurrency(summary.total_purchase_value_netto)} zł)` : 'Wartość magazynu netto (FIFO)'}
              opis={wgStanu ? `Stan z kartoteki wyceniony cenami zakupu z partii FIFO, czyli partie po odjęciu rozjazdu (${roznicaNetto >= 0 ? '+' : ''}${formatCurrency(roznicaNetto)} zł); bez kaucji` : 'SUM(ilość pozostała × cena zakupu netto) z partii; bez kaucji'} />
            <Kafelek kolor="purple" testId="kafelek-brutto" wartosc={`${formatCurrency(bruttoGlowne)} zł`}
              etykieta={wgStanu ? `Wartość brutto (wg partii: ${formatCurrency(summary.total_purchase_value_brutto)} zł)` : 'Wartość brutto (VAT produktu)'} opis="netto × (1 + stawka VAT produktu); stawka ujemna (zw) = 0%" />
            <Kafelek kolor="danger" testId="kafelek-sprzedaz" wartosc={`${formatCurrency(summary.total_sell_value_brutto)} zł`}
              etykieta="Wartość sprzedaży brutto" opis="Stan × cena sprzedaży brutto (dodatnie stany) – to nie jest wartość magazynu" />
          </div>

          {maNoweLinie && (
            <div data-testid="poza-wartoscia" style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-secondary)', border: '1px dashed var(--border)', borderRadius: '0.375rem', padding: '0.5rem 0.75rem' }}>
              <div style={{ fontWeight: '600', marginBottom: '0.25rem' }}>Poza wartością główną (nie wliczane):</div>
              <ul style={{ margin: 0, paddingLeft: '1.1rem', lineHeight: 1.6 }}>
                <li>stan bez partii FIFO – szacunek z kartoteki: <strong>{bezPartii.produkty}</strong> prod. / <strong>{formatQuantity(bezPartii.sztuki)}</strong> szt / <strong>{formatCurrency(bezPartii.netto)} zł</strong></li>
                <li>stany ujemne (sprzedane przed dostawą): <strong>{ujemne.produkty}</strong> prod. / <strong>{formatQuantity(ujemne.sztuki)}</strong> szt / <strong>{formatCurrency(ujemne.netto)} zł</strong></li>
                <li>różnica stan − partie (alert spójności): <strong>{rozjazd.produkty}</strong> prod. / <strong>{formatQuantity(rozjazd.sztuki)}</strong> szt / <strong>{formatCurrency(rozjazd.netto)} zł</strong></li>
                <li>opakowania/kaucje: <strong>{formatQuantity(summary.kaucje_szt)}</strong> szt ({summary.kaucje_produkty || 0} prod.)</li>
              </ul>
            </div>
          )}
        </div>
      </div>

      {/* Sklepy (firma = suma sklepow) */}
      {sklepy.length > 1 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
          <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
            <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>🏬 Wartość magazynu wg sklepów (firma = suma sklepów)</h5>
          </div>
          <div style={{ padding: '0.75rem', overflowX: 'auto' }}>
            <table data-testid="tabela-sklepy" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={th()}>Sklep</th>
                  <th style={th('right')}>Produktów</th>
                  <th style={th('right')}>Sztuk na stanie</th>
                  <th style={th('right')}>Wartość netto (wg stanu)</th>
                  <th style={th('right')}>Brutto</th>
                  <th style={th('right')}>Wg partii FIFO</th>
                  <th style={th('right')}>Bez partii (zł)</th>
                  <th style={th('right')}>Ujemne (szt)</th>
                  <th style={th('right')}>Rozjazd (prod.)</th>
                  <th style={th('right')}>Kaucje (szt)</th>
                </tr>
              </thead>
              <tbody>
                {sklepy.map((s) => (
                  <tr key={s.location_id} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td('left', { fontWeight: '500' })}>{s.nazwa}</td>
                    <td style={td('right')}>{s.produkty}</td>
                    <td style={td('right')}>{formatQuantity(s.wg_stanu ? s.wg_stanu.sztuki : s.sztuki)}</td>
                    <td style={td('right', { fontWeight: '600', color: 'var(--success)' })}>{formatCurrency(s.wg_stanu ? s.wg_stanu.netto : s.netto)} zł</td>
                    <td style={td('right')}>{formatCurrency(s.wg_stanu ? s.wg_stanu.brutto : s.brutto)} zł</td>
                    <td style={td('right')}>{formatCurrency(s.netto)} zł</td>
                    <td style={td('right')}>{formatCurrency(linia(s.bez_partii).netto)} zł</td>
                    <td style={td('right')}>{formatQuantity(linia(s.ujemne).sztuki)}</td>
                    <td style={td('right')}>{linia(s.rozjazd).produkty}</td>
                    <td style={td('right')}>{formatQuantity(s.kaucje_szt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Analiza wg kategorii */}
      {categories.length > 0 && (
        <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)', marginBottom: '0.75rem' }}>
          <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
            <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>📊 Wartość magazynu wg kategorii</h5>
          </div>
          <div style={{ padding: '0.75rem', overflowX: 'auto' }}>
            <table data-testid="tabela-kategorie" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={th()}>Kategoria</th>
                  <th style={th('right')}>Produktów</th>
                  <th style={th('right')}>Sztuk w partiach</th>
                  <th style={th('right')}>Wartość netto</th>
                  <th style={th('right')}>Brutto</th>
                </tr>
              </thead>
              <tbody>
                {categories.map((cat, idx) => (
                  <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                    <td style={td('left', { fontWeight: '500' })}>{cat.category_name}</td>
                    <td style={td('right')}>{cat.products_count}</td>
                    <td style={td('right')}>{formatQuantity(cat.total_quantity)}</td>
                    <td style={td('right', { fontWeight: '600', color: 'var(--success)' })}>{formatCurrency(cat.total_value_netto)} zł</td>
                    <td style={td('right')}>{cat.total_value_brutto != null ? `${formatCurrency(cat.total_value_brutto)} zł` : '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Lista produktów */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '0.375rem', backgroundColor: 'var(--card-bg)' }}>
        <div style={{ padding: '0.5rem', backgroundColor: 'var(--bg)', borderBottom: '1px solid var(--border)' }}>
          <h5 style={{ margin: 0, fontSize: '0.9rem', fontWeight: '600' }}>📋 Produkty o największej wartości magazynowej (top {products.length || 100})</h5>
        </div>
        <div style={{ padding: '0.75rem', overflowX: 'auto' }}>
          {products.length > 0 ? (
            <table data-testid="tabela-produkty" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={th()}>#</th>
                  <th style={th()}>Produkt</th>
                  <th style={th()}>Kod</th>
                  <th style={th()}>Kategoria</th>
                  {sklepy.length > 1 && <th style={th()}>Sklep</th>}
                  <th style={th('right')} title="stan_aktualny z pos_magazyn">Stan</th>
                  <th style={th('right')} title="Suma ilości pozostałej w partiach FIFO">W partiach</th>
                  <th style={th('right')} title="SUM(ilość pozostała × cena zakupu netto) z partii">Wartość netto FIFO</th>
                  <th style={th('right')} title="netto × (1 + VAT produktu)">Brutto</th>
                  <th style={th('right')} title="Cena zakupu netto z ostatniej partii (gdy brak – kartoteka)">Ostatnia cena</th>
                  <th style={th('right')} title="stan − w partiach (szt); ≠ 0 = niespójność">Różnica</th>
                </tr>
              </thead>
              <tbody>
                {products.map((product, idx) => {
                  const roznica = parseFloat(product.roznica_szt || 0);
                  const sklep = sklepy.find((s) => s.location_id === product.location_id);
                  return (
                    <tr key={`${product.id}-${product.location_id || ''}`} style={{ borderBottom: '1px solid var(--border)' }}>
                      <td style={td('left', { color: 'var(--text-muted)' })}>{idx + 1}</td>
                      <td style={td('left', { fontWeight: '500' })}>
                        {product.nazwa}
                        {product.bez_partii && <span style={{ marginLeft: '0.35rem', fontSize: '0.65rem', color: 'var(--orange)' }}>bez partii</span>}
                      </td>
                      <td style={td('left', { fontFamily: 'monospace', fontSize: '0.7rem' })}>{product.kod_produktu || product.ean || '-'}</td>
                      <td style={td()}>{product.kategoria_nazwa || '-'}</td>
                      {sklepy.length > 1 && <td style={td()}>{sklep?.nazwa || product.location_id || '-'}</td>}
                      <td style={td('right', { fontWeight: '600', color: parseFloat(product.quantity || 0) < 0 ? 'var(--danger)' : undefined })}>{formatQuantity(product.quantity)}</td>
                      <td style={td('right')}>{formatQuantity(product.sztuki_partie)}</td>
                      <td style={td('right', { fontWeight: '600', color: 'var(--success)' })}>{formatCurrency(product.stock_value_netto)} zł</td>
                      <td style={td('right')}>{formatCurrency(product.stock_value_brutto)} zł</td>
                      <td style={td('right')}>{formatCurrency(product.ostatnia_cena_netto ?? product.purchase_price_netto)} zł</td>
                      <td style={td('right', { fontWeight: roznica !== 0 ? '600' : '400', color: roznica !== 0 ? 'var(--danger)' : 'var(--text-muted)' })}>
                        {roznica === 0 ? '0' : `${roznica > 0 ? '+' : ''}${formatQuantity(roznica)}`}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          ) : (
            <div style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '2rem' }}>Brak produktów na magazynie</div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WarehouseReport;
